import React, { useState, useEffect, useCallback } from 'react';
import { usersAPI } from '../utils/api';
import { User, Shield, Clock, CheckCircle, XCircle, Eye, Edit3, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const [allRes, pendingRes] = await Promise.all([
        usersAPI.getAll(),
        usersAPI.getPending()
      ]);
      setUsers(allRes.data);
      setPendingUsers(pendingRes.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleApprove = async (userId, status) => {
    try {
      await usersAPI.approve(userId, status);
      toast.success(`User ${status === 'approved' ? 'approved' : 'rejected'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleUpdateUser = async (userId, field, value) => {
    try {
      await usersAPI.update(userId, { [field]: value });
      toast.success('User updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const formatDate = (date) => date ? new Date(date).toLocaleDateString() : 'Never';

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Approved</span>;
      case 'pending':
        return <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700">Pending</span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">Rejected</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Approved</span>;
    }
  };

  const getAccessIcon = (level) => {
    switch (level) {
      case 'view':
        return <Eye size={14} className="text-slate-500" />;
      case 'edit':
        return <Edit3 size={14} className="text-blue-500" />;
      case 'admin':
        return <Shield size={14} className="text-primary" />;
      default:
        return <Edit3 size={14} className="text-blue-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin text-primary" size={48} />
      </div>
    );
  }

  const pendingCount = pendingUsers.length;

  return (
    <div className="space-y-6" data-testid="users-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">User Management</h1>
        <p className="text-slate-500 mt-1">Manage user access, approvals, and permissions</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'all' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-all-users"
        >
          All Users ({users.length})
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'pending' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-pending-users"
        >
          <Clock size={16} />
          Pending Approval
          {pendingCount > 0 && (
            <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {/* Pending Approval Section */}
      {activeTab === 'pending' && (
        <div className="space-y-4">
          {pendingUsers.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
              <CheckCircle size={48} className="mx-auto text-green-400 mb-3" />
              <p className="text-slate-600">No pending approvals</p>
              <p className="text-sm text-slate-500 mt-1">All user registrations have been reviewed</p>
            </div>
          ) : (
            pendingUsers.map((user) => (
              <div 
                key={user._id} 
                className="bg-amber-50 border border-amber-200 rounded-lg p-6"
                data-testid={`pending-user-${user._id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-lg">
                      {user.full_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{user.full_name}</h3>
                      <p className="text-sm text-slate-600">{user.email}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Role: {user.role} • Registered: {formatDate(user.created_date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApprove(user._id, 'approved')}
                      className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      data-testid={`approve-btn-${user._id}`}
                    >
                      <CheckCircle size={16} />
                      Approve
                    </button>
                    <button
                      onClick={() => handleApprove(user._id, 'rejected')}
                      className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      data-testid={`reject-btn-${user._id}`}
                    >
                      <XCircle size={16} />
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* All Users Section */}
      {activeTab === 'all' && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">User</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Role</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Access Level</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Last Login</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Active</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr 
                  key={user._id} 
                  className="border-b border-slate-100 hover:bg-slate-50"
                  data-testid={`user-row-${user._id}`}
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                        {user.full_name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{user.full_name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1">
                      {user.role === 'Admin' && <Shield size={14} className="text-primary" />}
                      <span className="text-sm text-slate-600">{user.role}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {getStatusBadge(user.status)}
                  </td>
                  <td className="py-3 px-4">
                    <select
                      value={user.access_level || 'edit'}
                      onChange={(e) => handleUpdateUser(user._id, 'access_level', e.target.value)}
                      className="px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid={`access-level-${user._id}`}
                    >
                      <option value="view">👁 View Only</option>
                      <option value="edit">✏️ Edit</option>
                      <option value="admin">🛡️ Admin</option>
                    </select>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">
                    {formatDate(user.last_login)}
                  </td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => handleUpdateUser(user._id, 'active', !user.active)}
                      className={`px-3 py-1 text-xs rounded-full transition-colors ${
                        user.active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                      }`}
                      data-testid={`toggle-active-${user._id}`}
                    >
                      {user.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Access Level Legend */}
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
        <h3 className="font-semibold text-slate-900 mb-3">Access Level Guide</h3>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div className="flex items-start gap-2">
            <Eye size={16} className="text-slate-500 mt-0.5" />
            <div>
              <p className="font-medium text-slate-700">View Only</p>
              <p className="text-slate-500">Can view deals, clients, reports but cannot create or edit</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Edit3 size={16} className="text-blue-500 mt-0.5" />
            <div>
              <p className="font-medium text-slate-700">Edit</p>
              <p className="text-slate-500">Can create, edit deals, clients, activities</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Shield size={16} className="text-primary mt-0.5" />
            <div>
              <p className="font-medium text-slate-700">Admin</p>
              <p className="text-slate-500">Full access including user management and settings</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;
